// Ported verbatim from /Users/rs/dev/oq-images/src/lib/exportScene.ts.
// Keep behavior identical — see web/CLAUDE.md "Drop in to oq-images, don't reimagine".
import * as THREE from "three";

export type ExportOptions = {
  includeBackground: boolean;
  scale?: number;
};

export function renderToDataUrl(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
  opts: ExportOptions,
): string {
  const el = renderer.domElement;

  if (opts.includeBackground) {
    // For regular PNG output, use the already-rendered canvas. This is the
    // exact viewport the user is looking at, including the current camera,
    // aspect ratio, renderer size, and in-flight scene state.
    void scene;
    void camera;
    void opts.scale;
    return el.toDataURL("image/png");
  }

  const prevClearColor = new THREE.Color();
  renderer.getClearColor(prevClearColor);
  const prevClearAlpha = renderer.getClearAlpha();
  const ground = scene.getObjectByName("ground") as THREE.Object3D | undefined;
  const dotGrid = scene.getObjectByName("dot-grid") as THREE.Object3D | undefined;
  const prevGroundVisible = ground?.visible;
  const prevDotVisible = dotGrid?.visible;
  const prevBackground = scene.background;
  const prevFog = scene.fog;

  if (ground) ground.visible = false;
  if (dotGrid) dotGrid.visible = false;
  scene.background = null;
  scene.fog = null;
  renderer.setClearColor(0x000000, 0);

  renderer.render(scene, camera);
  const dataUrl = el.toDataURL("image/png");

  renderer.setClearColor(prevClearColor, prevClearAlpha);
  if (ground && prevGroundVisible !== undefined) ground.visible = prevGroundVisible;
  if (dotGrid && prevDotVisible !== undefined) dotGrid.visible = prevDotVisible;
  scene.background = prevBackground;
  scene.fog = prevFog;
  renderer.render(scene, camera);

  void opts.scale;
  return dataUrl;
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}

export async function copyToClipboard(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
  opts: ExportOptions,
): Promise<void> {
  const dataUrl = renderToDataUrl(renderer, scene, camera, opts);
  const blob = await dataUrlToBlob(dataUrl);
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
    throw new Error("Clipboard image write not supported in this browser");
  }
  await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
}

export function saveImage(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
  opts: ExportOptions,
  filename = "scene.png",
): void {
  const dataUrl = renderToDataUrl(renderer, scene, camera, opts);
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
